import React from 'react';
import { connect } from 'react-redux';
import { Routes as RouterRoutes, Route, Navigate } from 'react-router-dom';
import Loading from 'containers/Loading';
import NotFound from 'containers/NotFound';
import AccountsRedirect from 'containers/AccountsRedirect';
import Reporting from 'containers/Reporting';
import Rankings from 'containers/Rankings';
import Proposal from 'containers/Proposal';
import InitiativeDetail from 'containers/InitiativeDetail';
import CreateInitiative from 'containers/CreateInitiative';
import Initiative from 'containers/Initiative';
import Collaboration from 'containers/Collaboration';
import Volunteer from 'containers/Volunteer';
import Course from 'containers/Course';
import NotSubscribed from 'containers/NotSubscribed';
import ExternalManagerApproval from 'containers/ExternalManagerApproval';
import AboutOurProgram from 'containers/AboutOurProgram';

import { useFeatureList } from 'components/FeatureSwitch';
import { usePermissionList } from 'components/PermissionSwitch';

const ProtectedRoute = ({
  isLoggedIn,
  hasOrganizationRequirement,
  element,
}) => (
  isLoggedIn
  ? (
    hasOrganizationRequirement
    ? element
    : <Navigate to="/organizations" replace />
  ) :
    <Navigate to="/signin" replace />
);

const Routes = ({
  auth,
  organization,
}) => {
  const featureList = useFeatureList();
  const permissionList = usePermissionList();

  const defaultRoute = !auth.logged_in
    ? '/signin'
    : !organization.data
      ? '/organizations'
      : permissionList.has('can_read_reports') && featureList.has('dashboard')
        ? '/reporting'
        : '/initiative';

  return (
    <RouterRoutes>
      <Route path="/" element={<Navigate to={defaultRoute} replace />} />
      <Route path="/loading" element={<Loading.Big />} />
      <Route path="/signin" element={<AccountsRedirect />} />
      <Route path="/recover_password/:token" element={<AccountsRedirect />} />
      <Route path="/confirm_email/:token" element={<AccountsRedirect />} />
      <Route path="/signup/:token" element={<AccountsRedirect />} />
      <Route path="/recover_password" element={<AccountsRedirect />} />
      <Route path="/not_subscribed" element={<NotSubscribed />} />
      <Route path="/logout" element={<AccountsRedirect />} />
      <Route path="/manager_approval/:token" element={<ExternalManagerApproval />} />

      <Route
        path="/organizations"
        element={
          <ProtectedRoute
            isLoggedIn={auth.logged_in}
            hasOrganizationRequirement={true}
            element={<AccountsRedirect />}
          />
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute
            isLoggedIn={auth.logged_in}
            hasOrganizationRequirement={!!organization.data}
            element={<AccountsRedirect />}
          />
        }
      />

      { !permissionList.has('can_read_reports') || !featureList.has('dashboard')
        ? <Route path="/reporting" element={<Navigate to="/" replace />} />
        : <Route
            path="/reporting"
            element={
              <ProtectedRoute
                isLoggedIn={auth.logged_in}
                hasOrganizationRequirement={!!organization.data}
                element={<Reporting />}
              />
            }
          />
      }
      { !permissionList.has('can_read_reports') || !featureList.has('rankings')
        ? null
        : <Route
            path="/rankings"
            element={
              <ProtectedRoute
                isLoggedIn={auth.logged_in}
                hasOrganizationRequirement={!!organization.data}
                element={<Rankings />}
              />
            }
          />
      }
      { !permissionList.has('can_manage_initiatives')
        ? <Route path="/create/*" element={<Navigate to="/" replace />} />
        : <>
            <Route
              path="/create/:type"
              element={
                <ProtectedRoute
                  isLoggedIn={auth.logged_in}
                  hasOrganizationRequirement={!!organization.data}
                  element={<CreateInitiative />}
                />
              }
            />
            <Route
              path="/create"
              element={
                <ProtectedRoute
                  isLoggedIn={auth.logged_in}
                  hasOrganizationRequirement={!!organization.data}
                  element={<CreateInitiative />}
                />
              }
            />
          </>
      }
      <Route
        path="/initiative/tab/:tab"
        element={
          <ProtectedRoute
            isLoggedIn={auth.logged_in}
            hasOrganizationRequirement={!!organization.data}
            element={<Initiative />}
          />
        }
      />
      <Route
        path="/initiative/:slug/:action"
        element={
          <ProtectedRoute
            isLoggedIn={auth.logged_in}
            hasOrganizationRequirement={!!organization.data}
            element={<InitiativeDetail />}
          />
        }
      />
      <Route
        path="/initiative/:slug"
        element={
          <ProtectedRoute
            isLoggedIn={auth.logged_in}
            hasOrganizationRequirement={!!organization.data}
            element={<InitiativeDetail />}
          />
        }
      />
      <Route
        path="/proposal/:slug/:action"
        element={
          <ProtectedRoute
            isLoggedIn={auth.logged_in}
            hasOrganizationRequirement={!!organization.data}
            element={<InitiativeDetail />}
          />
        }
      />
      <Route
        path="/proposal/:slug"
        element={
          <ProtectedRoute
            isLoggedIn={auth.logged_in}
            hasOrganizationRequirement={!!organization.data}
            element={<InitiativeDetail />}
          />
        }
      />
      <Route
        path="/initiative"
        element={
          <ProtectedRoute
            isLoggedIn={auth.logged_in}
            hasOrganizationRequirement={!!organization.data}
            element={<Initiative />}
          />
        }
      />
      { !featureList.has('collaborations')
        ? null
        : !permissionList.has('can_manage_collaborations')
        ? <Route path="/collaboration/:slug/*" element={<Navigate to="/signin" replace />} />
        : <>
            <Route
              path="/collaboration/:slug/:action"
              element={
                <ProtectedRoute
                  isLoggedIn={auth.logged_in}
                  hasOrganizationRequirement={!!organization.data}
                  element={<InitiativeDetail />}
                />
              }
            />
            <Route
              path="/collaboration/:slug"
              element={
                <ProtectedRoute
                  isLoggedIn={auth.logged_in}
                  hasOrganizationRequirement={!!organization.data}
                  element={<InitiativeDetail />}
                />
              }
            />
          </>
      }
      { !featureList.has('collaborations')
        ? null
        : !permissionList.has('can_manage_collaborations')
        ? <Route path="/collaboration" element={<Navigate to="/" replace />} />
        : <Route
            path="/collaboration"
            element={
              <ProtectedRoute
                isLoggedIn={auth.logged_in}
                hasOrganizationRequirement={!!organization.data}
                element={<Collaboration />}
              />
            }
          />
      }
      { !featureList.has('proposals')
        ? null
        : !permissionList.has('can_manage_proposals')
        ? <Route path="/proposal" element={<Navigate to="/" replace />} />
        : <Route
            path="/proposal"
            element={
              <ProtectedRoute
                isLoggedIn={auth.logged_in}
                hasOrganizationRequirement={!!organization.data}
                element={<Proposal />}
              />
            }
          />
      }
      { !featureList.has('education')
        ? null
        : <Route
            path="/course"
            element={
              <ProtectedRoute
                isLoggedIn={auth.logged_in}
                hasOrganizationRequirement={!!organization.data}
                element={<Course />}
              />
            }
          />
      }
      { !permissionList.has('can_manage_volunteers') || !featureList.has('community')
        ? null
        : <Route
            path="/volunteer"
            element={
              <ProtectedRoute
                isLoggedIn={auth.logged_in}
                hasOrganizationRequirement={!!organization.data}
                element={<Volunteer />}
              />
            }
          />
      }
      { !featureList.has('about_our_program')
        ? null
        : <Route
            path="/about-our-program"
            element={
              <ProtectedRoute
                isLoggedIn={auth.logged_in}
                hasOrganizationRequirement={!!organization.data}
                element={<AboutOurProgram />}
              />
            }
          />
      }
      <Route
        path="/organization"
        element={
          <ProtectedRoute
            isLoggedIn={auth.logged_in}
            hasOrganizationRequirement={!!organization.data}
            element={<AccountsRedirect />}
          />
        }
      />
      <Route path="*" element={<NotFound />} />
    </RouterRoutes>
    )
}

const mapStateToProps = ({
  auth,
  organization,
}) => ({
  auth,
  organization,
});

export default connect(mapStateToProps, {})(Routes);
